/**
 * Centralised API route prefixes for the gateway.
 *
 * Every backend micro-service is reached through the gateway at:
 *   <base-url>/<SERVICE_PREFIX>/api/<VERSION>/…
 *
 * Add new services here — never hard-code prefixes in individual api files.
 */

// ── building blocks ──────────────────────────────────────────────────────────

/** Version segment shared by all services (bump when a v2 is introduced). */
const API_VERSION = 'v1';

/** Constructs a gateway prefix from a service name: `/<service>/api/<version>` */
function servicePrefix(service: string): string {
  return `/${service}/api/${API_VERSION}`;
}

// ── service prefixes ─────────────────────────────────────────────────────────

export const API_ROUTES = {
  /** tms-api-allergens — allergen & severity lookups */
  ALLERGENS: servicePrefix('allergens'),

  /** tms-api-facilities — locations, care-units, beds */
  FACILITIES: servicePrefix('facilities'),

  /** tms-api-patient — patients, assignments, milk-providers, links, documents */
  PATIENT: servicePrefix('patient'),

  /** tms-api-products — product catalogue & lookups */
  PRODUCTS: servicePrefix('products'),

  /** tms-api-milk-mgmt — milk batches, pools, pasteurisation */
  MILK_MANAGEMENT: servicePrefix('milkManagement'),

  /** tms-api-inventoryManagement — milk receivables, inventory tracking (port 5006) */
  INVENTORY_MANAGEMENT: servicePrefix('inventoryManagement'),

  /** tms-api-service — auth, staff, legacy endpoints */
  SERVICE: servicePrefix('service'),

  /** tms-api-feedManagement — feed administration, quick-feed, re-dosing */
  FEED_MANAGEMENT: servicePrefix('feedManagement'),

  /** tms-api-onboarding — tenant user onboarding (CSV import, etc.) */
  ONBOARDING: servicePrefix('onboarding'),

  /** tms-api-settings — help desk, app settings, smart search navigation index (port 5010) */
  SETTINGS: servicePrefix('settings'),

  /** tms-api-locale — localization / translation bundles (port 5011) */
  LOCALE: servicePrefix('locale'),

  /** tms-api-recall — recall management, affected patients, patient communications (port 5012) */
  RECALL: servicePrefix('recall'),

  /** tms-api-reports — feed-order reports, CSV/Excel export (port 5013) */
  REPORTS: servicePrefix('reports'),

  /** tms-api-notifications — real-time notifications, SSE stream, inbox (port 5300) */
  NOTIFICATIONS: servicePrefix('notifications'),

  /** Print agent — printer discovery, label print jobs */
  PRINT_AGENT: servicePrefix('printAgent'),
} as const;

/** Scanner-specific route constants under the PRINT_AGENT domain. */
export const PRINT_AGENT_SCANNER_ROUTES = {
  /** SSE stream: GET /printAgent/api/v1/scanner/events?tenantId=<tenantId> */
  EVENTS: `${API_ROUTES.PRINT_AGENT}/scanner/events`,
  /** Scan request: POST /printAgent/api/v1/scanner/scan */
  SCAN: `${API_ROUTES.PRINT_AGENT}/scanner/scan`,
} as const;

export type ServiceKey = keyof typeof API_ROUTES;
